import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Paths } from "src/paths";
import { barGeometry, type AxisTick, type Window } from "./scheduleTree";
import { type Channel } from "src/modules/engagement/components/phases/emailSms/emailSmsTypes";
import type { PlannedTouch } from "@services/sequence/sequenceService";

const CHANNEL: Record<Channel, { label: string; icon: string; color: string }> = {
  email: { label: "Email", icon: "bi bi-envelope-at", color: "#0e7490" },
  sms: { label: "SMS", icon: "bi bi-chat-dots", color: "#6d28d9" },
};

const BLOCKED = "#f59e0b";

/* A plain YYYY-MM-DD, read as a local day. `new Date("2027-01-04")` is parsed as UTC
   midnight, which renders as 3 January anywhere west of Greenwich. */
const parseDay = (value: string): Date => {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const fmt = (date: Date) =>
  date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export type TouchScheduleMark = {
  id: string;
  title: string;
  when: Date;
  blocked: string | null;
  sequenceName: string;
};

type Props = {
  engagementId: number;
  /** Resolved server-side: the send dates and block reasons the sender itself uses. */
  touches: PlannedTouch[];
  window: Window;
  ticks: AxisTick[];
  monthLines: number[];
  showDayLines: boolean;
  openChannels: Record<string, boolean>;
  onToggleChannel: (channel: Channel) => void;
};

/*
    Email and SMS on the schedule, read-only. A channel is one bar from its first send to its
    last, because that is the shape the rest of the chart uses and the question this page
    answers is "when is this campaign in market". Expanding gives a row per touch, and a
    touch is a dot rather than a bar: a send happens on a day, it does not run across one.

    Triggers are not editable here. They are set on the Email & SMS tab, and two screens
    writing the same offset is the ambiguity MPN-2040 removed from campaign dates.
*/
export default function TouchScheduleRows({
  engagementId,
  touches,
  window: chartWindow,
  ticks,
  monthLines,
  showDayLines,
  openChannels,
  onToggleChannel,
}: Props) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState<string | null>(null);

  /*
      Straight from the server. A touch with no send date has no key date on its campaign and
      so has no place on a calendar -- it is reported on the Email & SMS tab, where it can be
      fixed, rather than drawn here at a date nobody chose.
  */
  const marksFor = (channel: Channel): TouchScheduleMark[] =>
    touches
      .filter((touch) => touch.channel === channel && touch.send_date)
      .map((touch) => ({
        id: `${touch.launch_id}-${touch.sequence_id}-${touch.ad_id}`,
        title: touch.title,
        when: parseDay(touch.send_date as string),
        blocked: touch.blocked,
        sequenceName: touch.sequence_name,
      }))
      .sort((a, b) => a.when.getTime() - b.when.getTime());

  const trackBackground = showDayLines
    ? {
        backgroundImage: "linear-gradient(to right, #eef2f7 1px, transparent 1px)",
        backgroundSize: `${100 / chartWindow.totalDays}% 100%`,
      }
    : undefined;

  const gridLines = (
    <>
      {ticks.map((tick, i) => (
        <span key={`t${i}`} className="sched-track__line" style={{ left: `${tick.leftPct}%` }} />
      ))}
      {monthLines.map((left, i) => (
        <span
          key={`m${i}`}
          className="sched-track__line sched-track__line--month"
          style={{ left: `${left}%` }}
        />
      ))}
    </>
  );

  const goToTab = () =>
    navigate(`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${engagementId}/email-sms`);

  return (
    <>
      {(Object.keys(CHANNEL) as Channel[]).map((channel) => {
        const marks = marksFor(channel);
        if (marks.length === 0) return null;

        const meta = CHANNEL[channel];
        const open = !!openChannels[channel];
        const first = marks[0].when;
        const last = marks[marks.length - 1].when;
        const geo = barGeometry({ start: first, end: last }, chartWindow);
        const blockedCount = marks.filter((m) => m.blocked).length;

        return (
          <div key={channel}>
            <div className="sched-grid sched-row sched-row--group">
              <div className="sched-row__name">
                <button
                  type="button"
                  className="sched-row__chev"
                  aria-expanded={open}
                  aria-label={`${open ? "Collapse" : "Expand"} ${meta.label} touches`}
                  onClick={() => onToggleChannel(channel)}
                >
                  <i className={open ? "bi bi-chevron-down" : "bi bi-chevron-right"} />
                </button>
                <i className={meta.icon} style={{ color: meta.color }} />
                <span className="sched-row__text">{meta.label}</span>
                <i
                  className="bi bi-lock sched-row__readonly"
                  title="Read-only here. Triggers are set on the Email & SMS tab."
                />
                <span className="sched-row__count">
                  {marks.length} {marks.length === 1 ? "touch" : "touches"}
                  {blockedCount > 0 ? ` · ${blockedCount} blocked` : ""}
                </span>
              </div>

              <div className="sched-row__dates">
                <span className="sched-row__dates-main">
                  {fmt(first)} – {fmt(last)}
                </span>
                <button type="button" className="sched-row__link" onClick={goToTab}>
                  Email &amp; SMS tab
                  <i className="bi bi-arrow-up-right" />
                </button>
              </div>

              <div className="sched-track" style={trackBackground}>
                {gridLines}
                <span
                  className="sched-bar"
                  style={{
                    left: `${geo.leftPct}%`,
                    width: `${geo.widthPct}%`,
                    background: meta.color,
                  }}
                  title={`${meta.label}: ${marks.length} touches, ${fmt(first)} to ${fmt(last)}`}
                />
              </div>

              <span className="sched-row__go" aria-hidden="true" />
            </div>

            {open &&
              marks.map((mark) => {
                const markGeo = barGeometry({ start: mark.when, end: mark.when }, chartWindow);
                return (
                  <div
                    key={mark.id}
                    className="sched-grid sched-row sched-row--d2"
                    onMouseEnter={() => setHovered(mark.id)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <div className="sched-row__name">
                      <span className={`touch-ch touch-ch--${channel}`}>{meta.label}</span>
                      <span className="sched-row__text">{mark.title}</span>
                      {mark.blocked && (
                        <span className="sched-row__blocked" title={mark.blocked}>
                          Blocked
                        </span>
                      )}
                    </div>

                    <div className="sched-row__dates">{fmt(mark.when)}</div>

                    <div className="sched-track" style={trackBackground}>
                      {gridLines}
                      <span
                        className={`sched-mark${hovered === mark.id ? " sched-mark--hover" : ""}`}
                        style={{
                          left: `${markGeo.leftPct}%`,
                          background: mark.blocked ? BLOCKED : meta.color,
                        }}
                        title={`${mark.sequenceName} · ${mark.title} · ${fmt(mark.when)}${
                          mark.blocked ? ` · ${mark.blocked}` : ""
                        }`}
                      />
                    </div>

                    <span className="sched-row__go" aria-hidden="true" />
                  </div>
                );
              })}
          </div>
        );
      })}
    </>
  );
}
